// ESM App SDK protocol constants. The legacy CJS handshake keeps its own version in protocol.ts.
// Hosts, the bootstrap, plain-Node scripts and backend programs all import it, so its only
// dependency is configbase-common's portable apps contract, which native Node loads as well.
import { isTableMappings, type TableMappings } from '@doss/configbase-common/apps'

export const APP_SDK_PROTOCOL_VERSION = 1
export const APP_SDK_INTERNAL_PREFIX = '@doss/app-sdk/internal/'
export const APP_SDK_RUNTIME_ENTRY = `${APP_SDK_INTERNAL_PREFIX}runtime`

/** Customer-boundary aliases share the canonical module's exact artifact hash. */
export const APP_SDK_IMPORT_MIGRATIONS = {
  '@doss/app': '@doss/configbase/apps',
  '@doss/app/icons': '@doss/configbase/apps/icons',
  '@doss/arp': '@doss/configbase/apps',
  '@doss/arp/icons': '@doss/configbase/apps/icons',
  '@doss/guest-runtime': '@doss/configbase/apps',
} as const

/**
 * This binary's ESM-only public surface. The legacy CJS allowlist stays in protocol.ts.
 * This defines an SDK cut and the modules, but we don't want to accidentally use this list to
 * verify against a live SDK, which may have been frozen against a different list.
 */
export const UNPINNED_APP_SDK_PUBLIC_IMPORTS: ReadonlySet<string> = new Set([
  '@doss/configbase/common',
  '@doss/configbase/apps',
  '@doss/configbase/apps/icons',
  ...Object.keys(APP_SDK_IMPORT_MIGRATIONS),
  'react',
  'react/jsx-runtime',
  // [ENG-613] Remove from production once Agentation no longer relies on jsxDEV source metadata.
  'react/jsx-dev-runtime',
  'react-router',
  'echarts',
  'ag-grid-community',
  'ag-grid-enterprise',
  'ag-grid-react',
])

/**
 * Public CDN base for hash-addressed SDK assets, per deployed environment. Terraform owns the hosts
 * (infra/tf/<env>/assets-cdn.tf); this table mirrors them so no consumer hardcodes one
 * environment's host. Local development points `VITE_APP_SDK_ASSET_BASE` at a local build instead.
 */
export const APP_SDK_ASSET_BASES = {
  production: 'https://assets.doss.com/sdk/apps/',
  staging: 'https://staging.assets.doss.com/sdk/apps/',
} as const

export type AppSdkAssetEnvironment = keyof typeof APP_SDK_ASSET_BASES

/** The asset base an environment serves SDK assets from. */
export const appSdkAssetBaseFor = (environment: AppSdkAssetEnvironment): string =>
  APP_SDK_ASSET_BASES[environment]

/** Stored on the SDK patch-log entry; there is no separate App manifest or pin. */
export type AppSdkGraph = {
  protocolVersion: number
  runtimeEntry: string
  modules: Record<string, string>
  styles: string[]
}

/** Also used by the release store's zod schema. */
export const SHA256_HEX = /^[a-f0-9]{64}$/
export const APP_SDK_MODULE_ID = /^(?:@[a-z0-9-]+\/)?[a-z0-9-]+(?:\/[a-z0-9-]+)*$/

export const isSha256 = (value: unknown): value is string =>
  typeof value === 'string' && SHA256_HEX.test(value)

export const validateAppSdkGraph = (value: unknown): AppSdkGraph => {
  if (typeof value !== 'object' || value === null) {
    throw new Error('Missing App SDK graph')
  }
  const graph = value as { [Key in keyof AppSdkGraph]?: unknown }
  if (graph.protocolVersion !== APP_SDK_PROTOCOL_VERSION) {
    throw new Error('Unsupported App SDK protocol')
  }
  if (
    graph.runtimeEntry !== APP_SDK_RUNTIME_ENTRY ||
    typeof graph.modules !== 'object' ||
    graph.modules === null ||
    Array.isArray(graph.modules) ||
    !Array.isArray(graph.styles) ||
    graph.styles.length === 0 ||
    !graph.styles.every(isSha256)
  ) {
    throw new Error('Invalid App SDK graph')
  }
  const entries = Object.entries(graph.modules)
  if (
    entries.length > 128 ||
    entries.some(([id, hash]) => !isSha256(hash) || !APP_SDK_MODULE_ID.test(id))
  ) {
    throw new Error('Invalid App SDK module')
  }
  if (!Object.hasOwn(graph.modules, APP_SDK_RUNTIME_ENTRY)) {
    throw new Error(`Missing App SDK module: ${APP_SDK_RUNTIME_ENTRY}`)
  }
  return graph as AppSdkGraph
}

/** The host supplies this base from its SDK resolver. */
export const canonicalSdkBase = (value: string): string => {
  const url = new URL(value)
  if (
    (url.protocol !== 'https:' && !(url.protocol === 'http:' && url.hostname === '127.0.0.1')) ||
    url.username ||
    url.password ||
    url.search ||
    url.hash ||
    url.href !== value ||
    !/^\/[a-zA-Z0-9/_-]*\/$/.test(url.pathname)
  ) {
    throw new Error('App SDK asset base must be canonical HTTPS (loopback HTTP for local preview)')
  }
  return url.href
}

export const hashIntegrity = (hash: string): string => {
  if (!isSha256(hash)) {
    throw new Error('Invalid SHA-256')
  }
  return `sha256-${btoa(String.fromCharCode(...(hash.match(/../g) ?? []).map((byte) => parseInt(byte, 16))))}`
}

export const appSdkImportMap = (graph: AppSdkGraph, assetBase: string) => {
  validateAppSdkGraph(graph)
  const base = canonicalSdkBase(assetBase)
  const imports = Object.fromEntries(
    Object.entries(graph.modules).map(([id, hash]) => [id, `${base}${hash}.js`]),
  )
  const integrity = Object.fromEntries(
    Object.values(graph.modules).map((hash) => [`${base}${hash}.js`, hashIntegrity(hash)]),
  )
  return { imports, integrity }
}

export type AppFrameConfiguration = {
  expectedParentOrigin: string
  assetBase: string
  graph: AppSdkGraph
}

export type AppFrameInit = {
  type: 'app-init'
  protocolVersion: number
  annotationsEnabled?: boolean
  licenseKey?: string
  /**
   * The mount's URL state at handshake time (URL-synced hosts); absent = memory mode. Shape only
   * here; the bootstrap applies the guest navigation budgets before starting the runtime.
   */
  route?: { path: string; search: string }
  /** The version's deploy-resolved table mappings, installed as `__mappings` before the app loads. */
  mappings?: TableMappings
  /** The organization's IANA time zone, the zone its calendar dates are anchored in. */
  timeZone?: string
}

const isRouteShape = (value: unknown): value is { path: string; search: string } =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as { path?: unknown }).path === 'string' &&
  typeof (value as { search?: unknown }).search === 'string'

export const isAppFrameInit = (value: unknown): value is AppFrameInit => {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  const init = value as Partial<AppFrameInit>
  return (
    init.type === 'app-init' &&
    init.protocolVersion === APP_SDK_PROTOCOL_VERSION &&
    (init.annotationsEnabled === undefined || typeof init.annotationsEnabled === 'boolean') &&
    (init.licenseKey === undefined ||
      (typeof init.licenseKey === 'string' && init.licenseKey.length <= 10_000)) &&
    (init.route === undefined || isRouteShape(init.route)) &&
    (init.mappings === undefined || isTableMappings(init.mappings)) &&
    (init.timeZone === undefined ||
      (typeof init.timeZone === 'string' && init.timeZone.length <= 64))
  )
}

/** Frame → host, repeated until `app-init` arrives; then `app-sdk-ready` once the SDK graph loaded. */
export const isAppFrameReadyMessage = (
  value: unknown,
): value is { type: 'app-frame-ready'; protocolVersion: number } =>
  typeof value === 'object' &&
  value !== null &&
  (value as { type?: unknown }).type === 'app-frame-ready' &&
  typeof (value as { protocolVersion?: unknown }).protocolVersion === 'number'

export const isAppSdkReadyMessage = (value: unknown): value is { type: 'app-sdk-ready' } =>
  typeof value === 'object' &&
  value !== null &&
  (value as { type?: unknown }).type === 'app-sdk-ready'

export type AppBundleMessage = { type: 'app-bundle'; code: string; sha256: string }
export const isAppBundleMessage = (value: unknown): value is AppBundleMessage => {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  const bundle = value as Partial<AppBundleMessage>
  // ponytail: 10M UTF-16 code-unit ceiling; revisit with App size telemetry.
  return (
    bundle.type === 'app-bundle' &&
    typeof bundle.code === 'string' &&
    bundle.code.length > 0 &&
    bundle.code.length <= 10 * 1024 * 1024 &&
    isSha256(bundle.sha256)
  )
}
